import SearchableDropdown, {
  SearchableDropdownProps
} from '@components/searchable-dropdown/SearchableDropdown';
import TagSelector from '@components/tag-selector/TagSelector';
import { MAX_TAG_NAME_LENGTH } from '@constants/generic';
import { getRandomTagColor } from '@helpers/color';
import useNotify from '@hooks/notify';
import { Tag } from 'types/lead-management';

interface TagInputProps {
  value: string;
  onChange: (value: string) => void;
  onTagsChange: (tags: Tag[], type: 'add' | 'remove') => void;
  selectedTags: Tag[];
  tagOptions?: Tag[];
  onUpdateTag?: (tag: Tag) => void | boolean | Promise<void | boolean>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  searchIcon?: React.ReactNode;
  hideChevron?: boolean;
  renderSelectedBelow?: boolean;
  selectorProps?: Record<string, any>;
  renderChip?: SearchableDropdownProps<Tag>['renderChip'];
  chipPosition?: 'top' | 'bottom';
  defaultTagColor?: string;
  showSelectionTooltip?: boolean;
  keepOpenOnSelect?: boolean;
  nameLengthRange?: [number, number];
  searchable?: boolean;
}

const TagSelectorWrapper = ({
  onSelectItem,
  onCreateItem,
  onUpdateItem,
  selectedItems,
  itemOptions,
  showSelectionTooltip,
  ...otherProps
}: any) => (
  <TagSelector
    {...otherProps}
    onSelectTag={onSelectItem}
    onCreateTag={onCreateItem}
    onUpdateTag={onUpdateItem}
    selectedTags={selectedItems}
    tagOptions={itemOptions}
    showSelectionTooltip={showSelectionTooltip}
  />
);

const TagInput = ({
  value,
  onChange,
  onTagsChange,
  selectedTags,
  tagOptions = [],
  onUpdateTag,
  placeholder = 'Select/Search Tag',
  className = '',
  disabled = false,
  searchIcon,
  hideChevron,
  renderSelectedBelow,
  selectorProps,
  renderChip,
  chipPosition,
  defaultTagColor = '#F2E1E9',
  showSelectionTooltip = false,
  keepOpenOnSelect = false,
  nameLengthRange = [3, 30],
  searchable = true
}: TagInputProps) => {
  const { createErrorAlert } = useNotify();
  const [minLen, maxLen] = nameLengthRange;

  const handleCreateTag = (tagName: string, color?: string): Tag => ({
    id: `custom-${Date.now()}`,
    name: tagName,
    color: color || getRandomTagColor()
  });

  const validateNameLength = (name: string) => {
    if (name.length < minLen || name.length > maxLen) {
      createErrorAlert(
        `Name must be between ${minLen} and ${maxLen} characters`
      );
      return false;
    }
    return true;
  };

  return (
    <SearchableDropdown<Tag>
      value={value}
      onChange={onChange}
      onItemsChange={onTagsChange}
      selectedItems={selectedTags}
      availableItems={tagOptions}
      onUpdateItem={onUpdateTag}
      onCreateItem={handleCreateTag}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      allowCreate
      allowMultiple
      openOnFocus
      searchIcon={searchIcon}
      hideChevron={hideChevron}
      renderChip={renderChip}
      chipPosition={chipPosition}
      SelectorComponent={TagSelectorWrapper}
      validateNameLength={validateNameLength}
      renderSelectedBelow={renderSelectedBelow}
      selectorProps={selectorProps}
      defaultTagColor={defaultTagColor}
      showSelectionTooltip={showSelectionTooltip}
      keepOpenOnSelect={keepOpenOnSelect}
      searchable={searchable}
      maxInputLength={MAX_TAG_NAME_LENGTH}
    />
  );
};

export default TagInput;
